import { useEffect, useRef, useState, type RefObject } from "react"
import { usePage } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

/**
 * Paging for a list of cards. By default it is phone-only: a grid collapses to
 * one column there, so a dozen cards means a long scroll past everything else
 * on the page, and this shows PAGE_SIZE at a time with Previous / Next buttons
 * instead. From `sm` up nothing changes — every item is visible and the
 * controls are gone.
 *
 * Pass `phonePageSize` and paging applies at every width instead, with that
 * smaller page on phones: a list long enough to need paging on a phone is
 * usually long enough to need it on a laptop too.
 *
 * SSR-safe by construction: every item is always rendered, and the ones off
 * the current page only get a `max-sm:hidden` class, so the server, the
 * hydrating client and a crawler all agree on the markup (page one is what
 * everyone starts on). Phones are the only viewers that flip pages.
 *
 * Two presentations, chosen by the caller:
 * - `buttons` (default) — labelled Previous / Next either side of a range,
 *   sitting *below* a list of several cards. Paging scrolls the top of the
 *   list back into view, because the new cards render above the buttons.
 * - `compact` — a pair of arrow buttons and a position counter, meant to sit
 *   directly *under the heading* and above the list, one card to a page.
 *   Nothing scrolls: the controls are already at the top of the list the
 *   visitor is looking at.
 *
 * Wherever paging is live, a sideways swipe across the list turns the page as
 * well — the arrows stay, because a swipe is discoverable only once you've
 * tried it. With `edgeArrows`, the phone's pair of them moves onto the card's
 * own left and right edges (`edgeControls`, rendered inside a `relative`
 * wrapper around the list), pointing the way the finger goes.
 */
export const MOBILE_PAGE_SIZE = 3

export interface MobilePagerOptions {
  /** Items per page. Default `MOBILE_PAGE_SIZE`. */
  pageSize?: number
  /**
   * Items per page below `sm`. Setting it turns paging on at every width —
   * `pageSize` then means the wide-screen page. Leave it out for the
   * phone-only behaviour, where wide screens show the whole list.
   */
  phonePageSize?: number
  /** Control presentation — see the note above. Default `buttons`. */
  variant?: "buttons" | "compact"
  /**
   * Change this to send the reader back to page one — a filtered or searched
   * list is a different list, and holding page 9 of the old one shows them
   * whatever happens to be ninth in the new one.
   */
  resetKey?: string
  /** Layout classes for the compact controls, when they sit in a row of their own making. */
  controlsClassName?: string
  /**
   * Phone-only arrows tucked into the left and right edges of the card, as
   * `edgeControls`, instead of the pair under the heading — which leaves the
   * counter there on its own. Wider screens keep the compact arrows.
   */
  edgeArrows?: boolean
}

/**
 * Whether the viewport is at least `sm`. Starts true so the server, a crawler
 * and the hydrating client agree on the wide-screen page; a phone corrects it
 * on mount.
 */
function useWideViewport() {
  const [wide, setWide] = useState(true)

  useEffect(() => {
    const query = window.matchMedia("(min-width: 40rem)")
    const update = () => setWide(query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])

  return wide
}

/** How far a finger must travel sideways, in px, before it counts as a swipe. */
const SWIPE_THRESHOLD = 48

/**
 * Turn the page on a sideways swipe across the list. Listeners are attached to
 * the list element itself and are all passive — the gesture is judged when the
 * finger lifts, so a swipe never fights the page's own vertical scrolling, and
 * a flick that is mostly up or down is ignored outright.
 */
function useSwipePaging(
  listRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  onSwipe: (direction: 1 | -1) => void,
) {
  const handler = useRef(onSwipe)
  handler.current = onSwipe

  useEffect(() => {
    const list = listRef.current
    if (!enabled || !list) return

    let startX = 0
    let startY = 0
    let tracking = false

    const start = (event: TouchEvent) => {
      tracking = event.touches.length === 1
      if (!tracking) return
      startX = event.touches[0].clientX
      startY = event.touches[0].clientY
    }

    const end = (event: TouchEvent) => {
      if (!tracking) return
      tracking = false
      const touch = event.changedTouches[0]
      if (!touch) return

      const dx = touch.clientX - startX
      const dy = touch.clientY - startY
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return

      handler.current(dx < 0 ? 1 : -1)
    }

    const cancel = () => {
      tracking = false
    }

    list.addEventListener("touchstart", start, { passive: true })
    list.addEventListener("touchend", end, { passive: true })
    list.addEventListener("touchcancel", cancel, { passive: true })
    return () => {
      list.removeEventListener("touchstart", start)
      list.removeEventListener("touchend", end)
      list.removeEventListener("touchcancel", cancel)
    }
  }, [listRef, enabled])
}

export function useMobilePager(total: number, options: MobilePagerOptions = {}) {
  const {
    pageSize = MOBILE_PAGE_SIZE,
    phonePageSize,
    variant = "buttons",
    resetKey,
    controlsClassName,
    edgeArrows = false,
  } = options
  const everywhere = phonePageSize !== undefined
  const wide = useWideViewport()
  const size = everywhere && !wide ? phonePageSize : pageSize
  const [page, setPage] = useState(0)

  useEffect(() => {
    setPage(0)
  }, [resetKey, size])

  const listRef = useRef<HTMLElement | null>(null)
  const pageCount = Math.max(1, Math.ceil(total / size))
  const current = Math.min(page, pageCount - 1)

  const go = (next: number) => {
    setPage(Math.max(0, Math.min(pageCount - 1, next)))
    // Bring the top of the list back into view: the visitor tapped a button
    // below the last card, and the new cards render above it. Compact
    // controls sit above the list already, so moving the page would only
    // scroll them out of reach.
    if (variant === "buttons") listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Only where the page is actually being turned: a phone-only pager shows
  // every card from `sm` up, so a swipe on a tablet would move a counter and
  // nothing else.
  useSwipePaging(listRef, total > size && (everywhere || !wide), (direction) => go(current + direction))

  const Controls = variant === "compact" ? MobilePagerArrows : MobilePagerControls

  return {
    listRef,
    page: current,
    pageCount,
    /** Extra classes for item `index` — hides it unless it's on the current page. */
    itemClass: (index: number) =>
      Math.floor(index / size) === current ? "" : everywhere ? "hidden" : "max-sm:hidden",
    controls: total > size && (
      <Controls
        page={current}
        pageCount={pageCount}
        total={total}
        pageSize={size}
        onChange={go}
        className={controlsClassName}
        edgeArrows={edgeArrows}
      />
    ),
    /**
     * The phone's arrows, when `edgeArrows` is set: place them inside a
     * `relative` wrapper around the list and they ride its left and right
     * edges.
     */
    edgeControls: edgeArrows && total > size && (
      <MobilePagerEdgeArrows page={current} pageCount={pageCount} total={total} pageSize={size} onChange={go} />
    ),
  }
}

interface ControlProps {
  page: number
  pageCount: number
  total: number
  pageSize: number
  onChange: (page: number) => void
  className?: string
  /** Compact only: the phone's arrows live on the card edges instead. */
  edgeArrows?: boolean
}

function MobilePagerControls({ page, pageCount, total, pageSize, onChange }: ControlProps) {
  const { translations } = usePage<PageProps>().props
  const t = translations.common.pager
  const from = page * pageSize + 1
  const to = Math.min(total, (page + 1) * pageSize)

  return (
    <nav aria-label={t.label} className="mt-8 flex items-center justify-between gap-3 sm:hidden">
      <Button type="button" variant="secondary" disabled={page === 0} onClick={() => onChange(page - 1)}>
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t.previous}
      </Button>
      <span className="text-sm text-ink-muted" aria-live="polite">
        {t.showing.replace("%{from}", String(from)).replace("%{to}", String(to)).replace("%{total}", String(total))}
      </span>
      <Button type="button" variant="secondary" disabled={page >= pageCount - 1} onClick={() => onChange(page + 1)}>
        {t.next}
        <ChevronRight className="h-4 w-4" aria-hidden />
      </Button>
    </nav>
  )
}

/** Arrows + "1 of 4", sized to sit under a heading. */
function MobilePagerArrows({ page, pageCount, onChange, className, edgeArrows }: ControlProps) {
  const { translations } = usePage<PageProps>().props
  const t = translations.common.pager
  // With edge arrows, the phone's pair is on the card and only the counter
  // belongs here; wider screens have no card edges to hang them on.
  const hidden = edgeArrows ? "max-sm:hidden" : ""

  return (
    <nav aria-label={t.label} className={className ?? "mt-4 mb-6 flex items-center gap-2 sm:hidden"}>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={hidden}
        aria-label={t.previous}
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={hidden}
        aria-label={t.next}
        disabled={page >= pageCount - 1}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </Button>
      <span className="ml-1 text-sm text-ink-muted" aria-live="polite">
        {t.position.replace("%{current}", String(page + 1)).replace("%{total}", String(pageCount))}
      </span>
    </nav>
  )
}

/**
 * The phone's arrows, straddling the card's left and right edges: round, pale
 * and small, half over the card and half in the page gutter, so they point out
 * of the card the way the swipe goes and cover none of what it says. They are
 * the visible half of the gesture — a thumb reaching either edge finds one.
 */
function MobilePagerEdgeArrows({ page, pageCount, onChange }: ControlProps) {
  const { translations } = usePage<PageProps>().props
  const t = translations.common.pager
  const edge =
    "absolute top-1/2 z-10 h-9 w-9 -translate-y-1/2 rounded-full border-hairline bg-white/90 shadow-md backdrop-blur-sm sm:hidden"

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={`${edge} -left-3`}
        aria-label={t.previous}
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={`${edge} -right-3`}
        aria-label={t.next}
        disabled={page >= pageCount - 1}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </Button>
    </>
  )
}
